<template>
  <v2-modal
    :show="show"
    title="Add new retailer"
    size="md"
    @close="onClickCancel"
  >
    <p class="v2-text-sm v2-text-muted v2-mb-4">
      {{
        isFormDisabled
          ? 'This retailer exists in our platform. Please choose from the dropdown'
          : 'Complete all fields'
      }}
    </p>

    <!-- New retailer form -->
    <form
      v-if="!isFormDisabled"
      @submit.prevent="onSubmit(false)"
    >
      <div class="v2-flex v2-flex-col v2-gap-3">
        <v2-input
          v-model="form.entityName"
          label="Store Name"
          placeholder="Store Name"
          :readonly="isFormReadonly"
          :error="formErrors.entityName"
          @blur="validateField('entityName')"
        />
        <v2-input
          v-model="form.firstName"
          label="First Name"
          placeholder="First Name"
          :readonly="isFormReadonly"
          :error="formErrors.firstName"
          @blur="validateField('firstName')"
        />
        <v2-input
          v-model="form.lastName"
          label="Last Name"
          placeholder="Last Name"
          :readonly="isFormReadonly"
          :error="formErrors.lastName"
          @blur="validateField('lastName')"
        />
        <v2-input
          v-model="form.email"
          label="Email"
          placeholder="Email"
          type="email"
          :readonly="isFormReadonly"
          :error="formErrors.email"
          @blur="validateField('email')"
        />
        <div class="v2-input-wrapper">
          <label class="v2-input__label">Whatsapp Number</label>
          <vue-tel-input
            v-model="phoneNumber"
            :input-options="telInputOptions"
            mode="international"
            name="phone"
            valid-characters-only
            class="v2-tel-input"
            :class="{ 'v2-tel-input--disabled': isFormReadonly }"
            :disabled="isFormReadonly"
            :auto-default-country="autoDefaultCountry"
            :default-country="defaultCountry"
            @input="onInputPhoneNumber"
          />
          <small
            v-if="isNotValidPhoneNumber"
            class="v2-input__error"
          >Please enter a valid phone number</small>
        </div>
        <div
          v-if="countries.length"
          class="v2-input-wrapper"
        >
          <label class="v2-input__label">Country</label>
          <v-select
            v-model="form.country"
            :options="countries"
            placeholder="Country"
            :disabled="isFormReadonly"
            class="v2-v-select"
            @input="validateField('country')"
          />
          <small
            v-if="formErrors.country"
            class="v2-input__error"
          >{{ formErrors.country }}</small>
        </div>
      </div>

      <div class="v2-flex v2-justify-end v2-gap-2 v2-mt-6">
        <v2-button
          variant="outline"
          :disabled="isCreating"
          @click="onClickCancel"
        >
          Cancel
        </v2-button>
        <v2-button
          type="submit"
          :disabled="!isNewFormValid || isNotValidPhoneNumber || isCreating"
          :loading="isCreating"
        >
          Add retailer
        </v2-button>
      </div>
    </form>

    <!-- Existing retailer selection -->
    <div v-if="isFormDisabled">
      <div class="v2-input-wrapper v2-mb-4">
        <div class="v2-flex v2-justify-between v2-mb-1">
          <label class="v2-input__label">Select Retailers</label>
          <v2-badge variant="primary">
            {{ retailers.length }} Retailer{{ retailers.length > 1 ? 's' : '' }} selected
          </v2-badge>
        </div>
        <v-select
          v-model="retailers"
          :options="allRetailersLocal"
          multiple
          :close-on-select="false"
          label="entityName"
          placeholder="Search retailers..."
          class="v2-v-select-multi"
          :selectable="retailer => !retailer.isAdded"
          @search="searchRetailers"
        >
          <template #option="{ entityName, isAdded }">
            {{ isAdded ? '(added)' : '' }} {{ entityName }}
          </template>
        </v-select>
      </div>
      <div class="v2-flex v2-justify-end v2-gap-2">
        <v2-button
          variant="outline"
          :disabled="isCreating"
          @click="onClickCancel"
        >
          Cancel
        </v2-button>
        <v2-button
          :disabled="!retailers.length || isCreating"
          :loading="isCreating"
          @click="onSubmit(true)"
        >
          Add retailer
        </v2-button>
      </div>
    </div>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import vSelect from 'vue-select'
import { apiToastError } from '@/@core/utils/toast'
import { useAddUser } from '@/composables/useAddUser'

export default {
  name: 'V2AddUserModal',
  components: {
    V2Modal,
    V2Input,
    V2Button,
    V2Badge,
    vSelect,
  },
  props: {
    show: { type: Boolean, default: false },
  },
  setup() {
    return { ...useAddUser() }
  },
  data() {
    return {
      phoneNumber: '',
      autoDefaultCountry: true,
      defaultCountry: '',
      isValidPhoneNumber: false,
      telInputOptions: {
        id: 'v2_whatsapp_number',
        placeholder: 'Enter whatsapp number',
      },
    }
  },
  computed: {
    isNewFormValid() {
      return this.isFormValid
    },
    isNotValidPhoneNumber() {
      return this.form?.phone && !this.isValidPhoneNumber
    },
  },
  created() {
    this.loadCountries()
  },
  methods: {
    onInputPhoneNumber(number, phoneObject) {
      this.form.phone = number && phoneObject.nationalNumber
      this.form.countryCode = `+${phoneObject.countryCallingCode}`
      this.isValidPhoneNumber = !!phoneObject.valid
    },
    async onSubmit(shouldProceed = false) {
      const result = await this.submitForm(shouldProceed)
      if (result.success) {
        this.onClickCancel()
        this.$emit('load-connections')
      } else if (!result.is422) {
        apiToastError(result.error)
      }
    },
    onClickCancel() {
      this.phoneNumber = ''
      this.isValidPhoneNumber = false
      this.resetForm()
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
// vue-tel-input V2 theme (unscoped so it can target child elements)
.v2-tel-input {
  &.vue-tel-input {
    height: 2.714rem;
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-md);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    background: var(--v2-background);

    &:focus-within {
      border-color: var(--v2-primary);
      box-shadow: none;
    }

    .vti__dropdown {
      border-radius: var(--v2-radius-md) 0 0 var(--v2-radius-md);
    }

    .vti__dropdown-list {
      z-index: 200;
      border: 1px solid var(--v2-border);
      border-radius: var(--v2-radius-md);
      box-shadow: var(--v2-shadow-md);
    }

    .vti__input {
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);

      &::placeholder {
        color: var(--v2-muted-foreground);
      }
    }
  }

  &--disabled > * {
    cursor: not-allowed !important;
  }

  &--disabled > div.vti__dropdown:hover {
    background-color: unset !important;
  }
}

// vue-select V2 theme
.v2-v-select {
  &.v-select {
    .vs__dropdown-toggle {
      border: 1px solid var(--v2-input);
      border-radius: var(--v2-radius-md);
      background: var(--v2-background);
      min-height: 2.714rem;
      padding: 0;
      transition: border-color 0.15s ease;
    }

    &.vs--open .vs__dropdown-toggle {
      border-color: var(--v2-primary);
    }

    .vs__selected-options {
      padding: 0;
      flex-wrap: nowrap;
    }

    .vs__search {
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);
      margin: 0;
      padding: 6px 12px;
      line-height: 1.5;

      &::placeholder {
        color: var(--v2-muted-foreground);
      }
    }

    .vs__selected {
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);
      margin: 0;
      padding: 6px 12px;
      line-height: 1.5;
    }

    // When open, hide selected text so search input takes full space
    &.vs--open .vs__selected {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    .vs__actions {
      padding: 0 8px 0 4px;
    }

    .vs__dropdown-menu {
      border: 1px solid var(--v2-border);
      border-radius: var(--v2-radius-md);
      box-shadow: var(--v2-shadow-md);
      padding: var(--v2-space-1) 0;
      max-height: 200px;
    }

    .vs__dropdown-option {
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      padding: var(--v2-space-2) var(--v2-space-3);
      color: var(--v2-foreground);

      &--highlight {
        background: var(--v2-muted);
        color: var(--v2-foreground);
      }
    }

    .vs__clear,
    .vs__open-indicator {
      fill: var(--v2-muted-foreground);
    }

    &.vs--disabled {
      .vs__dropdown-toggle {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: var(--v2-muted);
      }
    }
  }
}

// vue-select V2 multiselect theme
.v2-v-select-multi {
  &.v-select {
    .vs__dropdown-toggle {
      border: 1px solid var(--v2-input);
      border-radius: var(--v2-radius-md);
      background: var(--v2-background);
      min-height: 2.714rem;
      padding: 4px 0 4px 8px;
      transition: border-color 0.15s ease;
    }

    &.vs--open .vs__dropdown-toggle {
      border-color: var(--v2-primary);
    }

    .vs__selected-options {
      padding: 0;
      flex-wrap: wrap;
      gap: 4px;
    }

    .vs__search {
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);
      margin: 0;
      padding: 2px 4px;

      &::placeholder {
        color: var(--v2-muted-foreground);
      }
    }

    .vs__selected {
      font-family: var(--v2-font-sans);
      font-size: 0.75rem;
      color: var(--v2-primary);
      background: rgba(116, 125, 238, 0.1);
      border: 1px solid rgba(116, 125, 238, 0.2);
      border-radius: 4px;
      margin: 0;
      padding: 2px 6px;
    }

    .vs__deselect {
      fill: var(--v2-primary);
      margin-left: 4px;
    }

    .vs__actions {
      padding: 0 8px 0 4px;
    }

    .vs__dropdown-menu {
      border: 1px solid var(--v2-border);
      border-radius: var(--v2-radius-md);
      box-shadow: var(--v2-shadow-md);
      padding: var(--v2-space-1) 0;
      max-height: 250px;
    }

    .vs__dropdown-option {
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      padding: var(--v2-space-2) var(--v2-space-3);
      color: var(--v2-foreground);

      &--highlight {
        background: var(--v2-muted);
        color: var(--v2-foreground);
      }

      &--selected {
        background: rgba(116, 125, 238, 0.05);
      }

      &--disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .vs__clear,
    .vs__open-indicator {
      fill: var(--v2-muted-foreground);
    }
  }
}
</style>

<style lang="scss" scoped>
.v2-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-1);
}

.v2-input__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v2-foreground);
}

.v2-input__error {
  font-size: 0.75rem;
  color: var(--v2-destructive);
}
</style>
